import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

export function FitBounds({ locations, fitKey, center }) {
  const map = useMap();
  const lastCenterRef = useRef(null);

  useEffect(() => {
    const doFit = () => {
      // Prefer fitting to actual pin locations over API-provided center
      if (locations.length > 0) {
        const lats = locations.map(l => l.lat).sort((a, b) => a - b);
        const lngs = locations.map(l => l.lng).sort((a, b) => a - b);
        const n = lats.length;
        if (n <= 4) {
          const bounds = L.latLngBounds(locations.map(l => [l.lat, l.lng]));
          map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14, animate: false });
        } else {
          const q1Lat = lats[Math.floor(n * 0.25)];
          const q3Lat = lats[Math.floor(n * 0.75)];
          const iqrLat = q3Lat - q1Lat;
          const q1Lng = lngs[Math.floor(n * 0.25)];
          const q3Lng = lngs[Math.floor(n * 0.75)];
          const iqrLng = q3Lng - q1Lng;
          const latMin = q1Lat - 1.5 * iqrLat;
          const latMax = q3Lat + 1.5 * iqrLat;
          const lngMin = q1Lng - 1.5 * iqrLng;
          const lngMax = q3Lng + 1.5 * iqrLng;
          const core = locations.filter(l =>
            l.lat >= latMin && l.lat <= latMax && l.lng >= lngMin && l.lng <= lngMax
          );
          const target = core.length > 0 ? core : locations;
          const bounds = L.latLngBounds(target.map(l => [l.lat, l.lng]));
          map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14, animate: false });
        }
      } else if (center) {
        // Fallback: no pins yet, use API center (ZIP-based)
        map.setView([center.lat, center.lng], 13, { animate: false });
        lastCenterRef.current = `${center.lat},${center.lng}`;
      }
    };
    // Immediate call + deferred retry to handle heavy marker rendering
    doFit();
    const timer = setTimeout(doFit, 200);
    return () => clearTimeout(timer);
    // Intentionally NOT depending on `locations.length`: the parent owns
    // when a re-fit is warranted (initial load, dataset change, manual
    // Refresh) and bumps `fitKey` to trigger it. Background auto-refresh
    // polls update `locations` periodically but must not yank the map
    // back to fit-all — that broke the user's current zoom/pan after
    // geocoding completion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, map]);
  return null;
}

export function InvalidateSize({ trigger }) {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 300);
  }, [trigger, map]);
  return null;
}

export function ZoomTracker({ onZoomChange, mapRefCb }) {
  const map = useMap();
  useEffect(() => {
    if (mapRefCb) mapRefCb(map);
    const handler = () => {
      const zoom = map.getZoom();
      onZoomChange(zoom);
      const scale = Math.pow(1.5, (zoom - 14));
      const clamped = Math.max(0.8, Math.min(3, scale));
      map.eachLayer((layer) => {
        if (layer.setRadius && layer._baseR) {
          layer.setRadius(layer._baseR * clamped);
        }
      });
    };
    map.on('zoomend', handler);
    handler();
    return () => map.off('zoomend', handler);
  }, [map, onZoomChange, mapRefCb]);
  return null;
}
